import { useCallback, useSyncExternalStore } from 'react';
import axios from 'axios';
import API_BASE from '../../config';

const LS_KEY = 'ts_universe';
const DEFAULT_ID = 'nifty_500';

/** Mirrors CURATED_INDICES in database/get_data.py. Kept in sync by hand: the
 *  list is short and changes rarely, and reading it needs a network round trip
 *  the first paint can't wait for. */
const CURATED = [
  'nifty_500',
  'nifty_100',
  'nifty_50',
  'nifty_next_50',
  'nifty_midcap_100',
  'nifty_smallcap_100',
  'fno',
  'nifty_bank',
];

// The universe list is curated server-side (CURATED_INDICES) and effectively
// static for a session, so it is fetched once and shared by every universe
// picker (chip popover, slot-machine wheel). `inflight` collapses concurrent
// mounts onto a single request.
export const universeIdsMemo = { value: null, inflight: null };

export function fetchUniverseIds() {
  if (universeIdsMemo.value) return Promise.resolve(universeIdsMemo.value);
  if (universeIdsMemo.inflight) return universeIdsMemo.inflight;
  universeIdsMemo.inflight = axios
    .get(`${API_BASE}/stocks/indices`)
    .then(({ data }) => {
      // Server order is meaningful (broad → narrow) — never re-sort it.
      const list = data?.indices || [];
      universeIdsMemo.value = list;
      universeIdsMemo.inflight = null;
      return list;
    })
    .catch((err) => {
      universeIdsMemo.inflight = null;
      throw err;
    });
  return universeIdsMemo.inflight;
}

// Band membership (mirrors TerminalLayout's chip popover) — tradability/sector
// filters first, then the size ladder ascending. Editorial, not server order.
const UNIVERSE_BANDS = [
  ['fno', 'nifty_bank'],
  ['nifty_50', 'nifty_100', 'nifty_500'],
  ['nifty_next_50', 'nifty_midcap_100', 'nifty_smallcap_100'],
];

/** Group ids into bands, ordering each band as declared above rather than as
 *  the server sent it. Anything the bands don't know about still renders, in a
 *  trailing group, so a newly-curated index is never swallowed by the UI. */
export function groupUniverses(ids) {
  const available = new Set(ids);
  const seen = new Set();
  const groups = UNIVERSE_BANDS.map((band) => {
    const members = band.filter((id) => available.has(id));
    members.forEach((id) => seen.add(id));
    return members;
  }).filter((members) => members.length);

  const rest = ids.filter((id) => !seen.has(id));
  return rest.length ? [...groups, rest] : groups;
}

/** id → display label ("nifty_500" → "Nifty 500", "fno" → "F&O"). */
export function universeLabel(id) {
  if (!id) return 'Nifty 500';
  if (id === 'fno') return 'F&O';
  return id
    .split('_')
    .map((w) => (/^\d/.test(w) ? w : w === 'it' ? 'IT' : w === 'psu' ? 'PSU' : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

export function currentUniverseId() {
  try {
    const saved = localStorage.getItem(LS_KEY);
    // A device still holding a universe we retired (a sector one, or "Other",
    // which resolved to zero triggers) would otherwise be stuck on a terminal
    // that never populates and no longer offers that option in the picker.
    if (saved && !CURATED.includes(saved)) return DEFAULT_ID;
    return saved || DEFAULT_ID;
  } catch {
    return DEFAULT_ID;
  }
}

// One value shared by every call site. Each consumer used to hold its own
// useState, so changing the universe in the topbar left sibling pages (and the
// effects keyed on universe.id) reading the previous value until a remount —
// a Nifty 100 chip over a list still scoped to the old universe.
let current = null;
const listeners = new Set();

function getSnapshot() {
  if (current === null) current = currentUniverseId();
  return current;
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Cross-tab: another tab's pick should not leave this one out of step. */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key !== LS_KEY) return;
    const next = currentUniverseId();
    if (next === current) return;
    current = next;
    listeners.forEach((fn) => fn());
  });
}

/** The user's universe (mockup Universe chip). Persists per device. */
export default function useUniverse() {
  const id = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const set = useCallback((next) => {
    if (next === current) return;
    current = next;
    try {
      localStorage.setItem(LS_KEY, next);
    } catch { /* in-memory only */ }
    listeners.forEach((fn) => fn());
  }, []);
  return { id, label: universeLabel(id), set };
}
